import { Location } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { VerificationStatus } from '@shure/cloud/shared/services/organizations-store-service';
import { OrganizationDetails } from '@shure/cloud/shared/switch-organization/data-access';
import { generateColorFromName } from '@shure/cloud/shared/ui/components';
import {
	PRISM_DIALOG_DATA,
	PrismDialogRef,
	PrismDialogService,
	PrismSnackbarService
} from '@shure/prism-angular-components';

import { OrganizationList } from '../../models/switch-organization.model';
import { SwitchOrganizationService } from '../../services/switch-organization.service';

@Component({
	selector: 'sh-change-organization-dialog',
	templateUrl: './change-organization-dialog.component.html',
	styleUrl: './change-organization-dialog.component.scss',
	standalone: false
})
export class ChangeOrganizationDialogComponent implements OnInit {
	public organizationsResponseList!: OrganizationList[];
	public organizationsList!: OrganizationList[];
	public defaultOrgId!: string;
	public selectedOrganization!: OrganizationDetails;
	public readonly toggleLoader = signal(true);
	public verificationStatus = VerificationStatus;
	private destroy$: Subject<void> = new Subject<void>();
	constructor(
		private location: Location,
		private switchOrganizationService: SwitchOrganizationService,
		public dialogRef: PrismDialogRef<ChangeOrganizationDialogComponent>,
		public prismDialogService: PrismDialogService,
		private readonly destroyRef: DestroyRef,
		private snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService,
		private readonly oktaIntfService: OktaInterfaceService,
		@Inject(PRISM_DIALOG_DATA) public dialogData: { code?: string }
	) {}
	public ngOnInit(): void {
		this.oktaIntfService
			.getDefaultOrgId$()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((val: string) => {
				this.defaultOrgId = val;
			});
		this.getAllMyOrganizations();
	}

	public getAllMyOrganizations(): void {
		this.switchOrganizationService
			.getAllMyOrganizations()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (organziationListResp) => {
					this.organizationsList = organziationListResp.body?.body?.items || [];
					this.moveCurrentOrgToTop();
					this.organizationsList = this.organizationsList.map((org) => {
						return {
							...org,
							companyName: org.companyName || '--',
							profileColor: generateColorFromName(org.name ?? ''),
							initials: this.generateInitials(org.name ?? '')
						};
					});
					this.organizationsResponseList = JSON.parse(JSON.stringify(this.organizationsList));
				},
				error: (error) => {
					const errorMessage = error.error;
					this.snackBarService.open({
						message: this.translocoService.translate(
							errorMessage?.i18nKey
								? 'cloud.shared.error-labels.' + errorMessage.i18nKey
								: errorMessage.message
						),
						actionText: this.translocoService.translate('common.snackbar.ok')
					});
				},
				complete: () => {
					this.toggleLoader.set(false);
				}
			});
	}

	/**
	 * Function to move the default organization to the top of the organizations list.
	 */
	public moveCurrentOrgToTop(): void {
		const defaultOrgIndex = this.organizationsList.findIndex(
			(organization) => organization.id === this.defaultOrgId
		);
		if (defaultOrgIndex !== -1) {
			const defaultOrganizationDetails = this.organizationsList.splice(defaultOrgIndex, 1);
			this.organizationsList.unshift(defaultOrganizationDetails[0]);
		}
	}
	/**
	 * This function will close the change organization dialog and open the create organization dialog.
	 */
	public openCreateOrganization(): void {
		this.switchOrganizationService.setReInitiateAddLicenses(false);
		this.prismDialogService.getDialogById('change-org-dialog')?.close();
		const createDialogRef = this.switchOrganizationService.openCreateOrgDialog();

		createDialogRef.afterClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.dialogData?.code) {
				this.switchOrganizationService.setReInitiateAddLicenses(true);
			}
		});
	}

	/**
	 * This will close the dialog when user clicks on cancel
	 */
	public closeDialog(): void {
		this.dialogRef.close(true);
	}

	/**
	 * This function will return the selected org data from the organization list.
	 */
	public selectOrganization(_event: Event, org: OrganizationDetails): void {
		if (org && this.defaultOrgId !== org.id) {
			this.selectedOrganization = org;
		}
	}

	/**
	 * This function will filter the organizations list
	 * It is called for every single list item  to check if the search string is present in the list
	 * @param searchKey - search text with which the list is filtered
	 */
	public applyFilter(searchKey: string): void {
		const filterValue = searchKey.trim().toLowerCase();
		this.organizationsList = this.organizationsResponseList.filter(
			(org) =>
				org?.name?.toLowerCase().includes(filterValue) || org?.companyName?.toLowerCase().includes(filterValue)
		);
	}

	/**
	 * This function will return the initials for the organization
	 * @param orgName - organization name as a string
	 */
	public generateInitials(orgName: string): string {
		return orgName.split(' ')[0].charAt(0).toUpperCase() + (orgName.split(' ')[1]?.charAt(0).toUpperCase() || '');
	}

	/**
	 * Function to change the selected organization and reload the window.
	 */
	public changeOrganization(): void {
		if (this.selectedOrganization.id) {
			const code = this.dialogData?.code;

			if (code !== undefined && code !== null) {
				const currentPath = window.location.pathname;
				const searchParams = new URLSearchParams(window.location.search);

				// Add or update query param
				searchParams.set('code', code);

				// Apply new URL (without triggering navigation)
				this.location.go(currentPath, searchParams.toString());
			}
			this.switchOrganizationService.changeOrganization(this.selectedOrganization.id);
		}
	}
}
